import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { GIButton, Modal } from 'app-components';

import { ConditionalRendering } from '../../../components/common/conditional-rendering';
import { RELATIONSHIP_TYPES } from '../../../constants/common/relationShipTypes';
import { ModalContextType } from '../../../contexts/modalContext';
import { cloneObject } from '../../../utils/miscellaneous/cloneObject';
import CapitalizedButton from '../components/common/CapitalizedButton';

interface IEnrollee {
    name: string;
    startDate: string;
    endDate: string;
    relationship: string;
    exchgIndivIdentifier: string;
    checked: false;
    disable: true;
}

interface IModalProps extends ModalContextType {
    enrolles: Array<IEnrollee>;
    showAllMembersText: boolean;
    setMemberGuidsToDisenroll: Function;
    subscriberName: string;
}

const StyledContainer = styled.div`
    border-left: 3px solid ${({ theme }) => theme.infoMain} !important;
    color : ${({ theme }) => theme.infoDarker} !important;
    background : ${({ theme }) => theme.infoLightest} !important;
`;
const StyledEnrolleeContainer = styled.div`
    .display-label-center{
        display: flex;
        align-items: center;
    }
`;

export default function DisEnrollMembers({
    enrolles,
    showAllMembersText,
    setMemberGuidsToDisenroll,
    closeModal,
    subscriberName,
}: IModalProps) {
    const [enrollees, setEnrollees] = useState(cloneObject(enrolles));
    const intl = useIntl();
    const helpText = `${intl.formatMessage({ id: 'indportal.plansummary.whoNeedsToBeDisEnrolled.helpText' }, { subscriberName })}`;

    function onClose(e) {
        e?.preventDefault();
        closeModal('disenrollMembers');
    }

    function handleEnrolleeSelect(isChecked: boolean, enrollee: IEnrollee): void {
        const isSelf = enrollee.relationship.toUpperCase() === RELATIONSHIP_TYPES.SELF.value;
        const newEnrollees = enrollees.map(object => {
            const isObjectSelf = object.relationship.toUpperCase() === RELATIONSHIP_TYPES.SELF.value;
            if (isSelf) {
                // If the selected enrollee is SELF
                return { ...object, checked: isObjectSelf && isChecked };
            } else if (object.exchgIndivIdentifier === enrollee.exchgIndivIdentifier) {
                // If the selected enrollee matches the current object
                return { ...object, checked: isChecked };
            } else if (isObjectSelf) {
                // If the object is SELF and a different enrollee is selected
                return { ...object, checked: false };
            }
            return object;
        });
        setEnrollees(newEnrollees);
    }

    function handleContinue() {
        setMemberGuidsToDisenroll(getDisEnrollMemberId());
        closeModal('disenrollMembers');
    }

    const getDisEnrollMemberId = () => {
        const memberIds: string[] = [];
        for (const enrollee of enrollees) {
            if (enrollee.checked) {
                memberIds.push(enrollee.exchgIndivIdentifier);
            }
        }
        return memberIds;
    };

    const getEnrollInputBoxes = (): React.ReactElement => {
            return (
            <>
                {enrollees.map((enrollee: IEnrollee) => (
                <StyledEnrolleeContainer key={enrollee.exchgIndivIdentifier}>
                        <ConditionalRendering
                            isTrue={enrollee.relationship.toUpperCase() !== RELATIONSHIP_TYPES.SELF.value}
                        >
                            <label className="display-label-center">
                            <input
                                type="checkbox"
                                id={`member_${enrollee.exchgIndivIdentifier}`}
                                name={`member_${enrollee.exchgIndivIdentifier}`}
                                value={enrollee.exchgIndivIdentifier}
                                    onChange={e => handleEnrolleeSelect(e.target.checked, enrollee)}
                                    className="margin-r-10"
                                checked={enrollee.checked}
                                disabled={enrollee.disable}
                            />
                            {enrollee.name} &nbsp;&nbsp;
                            <ConditionalRendering isTrue={enrollee.disable}>
                                <small>
                                    <span>
                                        <FormattedMessage id="indportal.enrollments.pendingTermination" />
                                    </span>
                                </small>
                            </ConditionalRendering>
                        </label>
                    </ConditionalRendering>
                    </StyledEnrolleeContainer>
                ))}
                {enrollees.map(enrollee => (
                    <StyledEnrolleeContainer key={enrollee.exchgIndivIdentifier}>
                        <ConditionalRendering
                            isTrue={
                                enrollee.relationship.toUpperCase() === RELATIONSHIP_TYPES.SELF.value &&
                                enrolles.length > 1 &&
                                showAllMembersText
                            }
                        >
                            <label className="display-label-center">
                            <input
                                type="checkbox"
                                    onChange={e => handleEnrolleeSelect(e.target.checked, enrollee)}
                                    className="margin-r-10"
                                id={`member_${enrollee.exchgIndivIdentifier}`}
                                checked={enrollee.checked}
                                value={enrollee.exchgIndivIdentifier}
                            />
                                <FormattedMessage id="indportal.enrollments.allMembers" /> &nbsp;&nbsp;
                            </label>
                    </ConditionalRendering>
                        <ConditionalRendering
                            isTrue={
                                enrollee.relationship.toUpperCase() === RELATIONSHIP_TYPES.SELF.value &&
                                (enrolles.length === 1 || !showAllMembersText)
                            }
                        >
                            <label className="display-label-center">
                            <input
                                type="checkbox"
                                    onChange={e => handleEnrolleeSelect(e.target.checked, enrollee)}
                                    className="margin-r-10"
                                id={`member_all`}
                                checked={enrollee.checked}
                                value={enrollee.exchgIndivIdentifier}
                            />
                                {enrollee.name} &nbsp;&nbsp;
                            </label>
                    </ConditionalRendering>
                </StyledEnrolleeContainer>
                ))}
            </>
        );
    };

    return (
        <Modal id="disenrollMembersModal">
            <Modal.Header
                id="disenrollMembersModalHeader"
                titleId="indportal.plansummary.voluntarydisenrollment"
                onHide={onClose}
            />
            <Modal.Body id="disenrollMembersModalBody">
                <h5 className="no-border margin0-b">
                    <span
                        dangerouslySetInnerHTML={{
                            __html: `${intl.formatMessage({ id: 'indportal.plansummary.whoNeedsToBeDisEnrolled' })}`,
                        }}
                    ></span>
                </h5>
                <div className="minh-10">{getEnrollInputBoxes()}</div>
                <StyledContainer className="alert" dangerouslySetInnerHTML={{ __html: helpText }}></StyledContainer>
            </Modal.Body>
            <Modal.Controls>
                <CapitalizedButton 
                    id="disenrollMembersModalCancelButton" 
                    onClickHandler={onClose} 
                    variant="secondary" 
                    labelId="button.cancel" 
                />
                <CapitalizedButton 
                    id="disenrollMembersModalContinueButton" 
                    onClickHandler={handleContinue} 
                    variant="primary" 
                    labelId="button.continue" 
                    leftMargin={true} 
                />
            </Modal.Controls>
        </Modal>
    );
}
